import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Download } from "lucide-react";
import { toast } from "sonner";

const CARD_OPTIONS = [
  { key: "total_lent", label: "Total Money Lent" },
  { key: "total_received", label: "Repayments Received" },
  { key: "profit_earned", label: "Interest / Profit" },
  { key: "principal_collected", label: "Principal Collected" },
  { key: "interest_collected", label: "Interest Collected" },
  { key: "today_collection", label: "Today's Collection" },
  { key: "weekly_collection", label: "Weekly Collection" },
  { key: "cash_collected", label: "Cash Collected" },
  { key: "upi_collected", label: "UPI Collected" },
  { key: "outstanding", label: "Outstanding Balance" },
  { key: "overdue", label: "Overdue Amount" },
  { key: "active_borrowers", label: "Active Borrowers" },
];

export default function Settings() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (settings) setForm({ ...settings, default_interest_rate: settings.default_interest_rate ?? 30 }); }, [settings]);

  if (!form) return <div className="text-slate-400">Loading…</div>;

  const set = (k, v) => setForm({ ...form, [k]: v });

  const toggleCard = (key) => {
    const cur = form.visible_cards || [];
    set("visible_cards", cur.includes(key) ? cur.filter((c) => c !== key) : [...cur, key]);
  };

  const save = async () => {
    const rate = Number(form.default_interest_rate);
    if (String(form.default_interest_rate).trim() === "" || !Number.isFinite(rate) || rate < 1 || rate > 100) {
      toast.error("Interest rate must be between 1% and 100%"); return;
    }
    setSaving(true);
    try {
      await api.put("/settings", form);
      await refresh();
      toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const exportBackup = async () => {
    const r = await api.get("/backup/export");
    const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `finlend-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Backup downloaded");
  };

  const card = (title, children) => (
    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">
      <h2 className="font-heading font-semibold text-navy border-l-4 border-maroon pl-3">{title}</h2>
      {children}
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">Settings</h1>
          <p className="text-slate-500 mt-1">Configure your dashboard and defaults</p>
        </div>
        <Button className="bg-maroon hover:bg-maroon-hover" onClick={save} disabled={saving} data-testid="save-settings-button">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save Changes"}
        </Button>
      </div>

      {card("General", (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Business Name (Dashboard)</Label>
            <Input value={form.business_name || ""} onChange={(e) => set("business_name", e.target.value)} data-testid="settings-business-name" /></div>
          <div><Label>Currency Symbol</Label>
            <Input value={form.currency || "₹"} onChange={(e) => set("currency", e.target.value)} data-testid="settings-currency" /></div>
          <div><Label>Date Format</Label>
            <Select value={form.date_format} onValueChange={(v) => set("date_format", v)}>
              <SelectTrigger data-testid="settings-date-format"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Default Interest Rate (%)</Label>
            <Input type="number" min="1" max="100" step="0.01" value={form.default_interest_rate ?? 30} onChange={(e) => set("default_interest_rate", e.target.value)} data-testid="settings-interest-rate" />
            <p className="text-xs text-slate-500 mt-1">Flat interest for the full loan term. Applies to new loans and renewals. Example: 30% on 10,000 gives 13,000 total.</p></div>
          <div><Label>Default Loan Duration ({form.default_frequency === "monthly" ? "months" : "weeks"})</Label>
            <Input type="number" value={form.default_duration_weeks} onChange={(e) => set("default_duration_weeks", parseInt(e.target.value) || 10)} data-testid="settings-default-duration" /></div>
          <div><Label>Default Repayment Frequency</Label>
            <Select value={form.default_frequency} onValueChange={(v) => set("default_frequency", v)}>
              <SelectTrigger data-testid="settings-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      ))}

      {card("Receipt & Invoice Numbering", (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Loan ID Prefix</Label>
            <Input value={form.loan_id_prefix || ""} onChange={(e) => set("loan_id_prefix", e.target.value)} data-testid="settings-loan-prefix" /></div>
          <div><Label>Receipt Prefix</Label>
            <Input value={form.receipt_prefix || ""} onChange={(e) => set("receipt_prefix", e.target.value)} data-testid="settings-receipt-prefix" /></div>
        </div>
      ))}

      {card("Payment Methods", (
        <div>
          <Input value={(form.payment_methods || []).join(", ")}
            onChange={(e) => set("payment_methods", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
            placeholder="Cash, UPI" data-testid="settings-payment-methods" />
          <p className="text-xs text-slate-400 mt-1">Comma-separated list of payment methods.</p>
        </div>
      ))}

      {card("Dashboard Cards (Show / Hide)", (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {CARD_OPTIONS.map((c) => (
            <div key={c.key} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
              <span className="text-sm font-medium text-navy">{c.label}</span>
              <Switch checked={(form.visible_cards || []).includes(c.key)} onCheckedChange={() => toggleCard(c.key)} data-testid={`toggle-card-${c.key}`} />
            </div>
          ))}
        </div>
      ))}

      {card("Typing & Appearance", (
        <div className="space-y-4">

          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
            <div>
              <div className="text-sm font-medium text-navy">Auto Spell Check</div>
              <p className="text-xs text-slate-400 mt-0.5">
                Highlights possible spelling mistakes while typing.
              </p>
            </div>
            <Switch
              checked={form.spell_check !== false}
              onCheckedChange={(v) => set("spell_check", v)}
              data-testid="settings-spell-check"
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
            <div>
              <div className="text-sm font-medium text-navy">Auto Correct</div>
              <p className="text-xs text-slate-400 mt-0.5">
                Allows supported devices to automatically correct typing mistakes.
              </p>
            </div>
            <Switch
              checked={form.auto_correct !== false}
              onCheckedChange={(v) => set("auto_correct", v)}
              data-testid="settings-auto-correct"
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
            <div>
              <div className="text-sm font-medium text-navy">Auto Capitalization</div>
              <p className="text-xs text-slate-400 mt-0.5">
                Automatically capitalizes sentences on supported devices.
              </p>
            </div>
            <Switch
              checked={form.auto_capitalize !== false}
              onCheckedChange={(v) => set("auto_capitalize", v)}
              data-testid="settings-auto-capitalize"
            />
          </div>

          <div className="rounded-lg border border-slate-200 px-4 py-4">
            <Label>FinLend Font Style</Label>

            <Select
              value={form.font_style || "inter"}
              onValueChange={(v) => set("font_style", v)}
            >
              <SelectTrigger
                className="mt-2"
                data-testid="settings-font-style"
              >
                <SelectValue placeholder="Select font" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="inter">
                  Inter
                </SelectItem>

                <SelectItem value="outfit">
                  Outfit
                </SelectItem>

                <SelectItem value="arial">
                  Arial
                </SelectItem>

                <SelectItem value="georgia">
                  Georgia
                </SelectItem>

                <SelectItem value="times">
                  Times New Roman
                </SelectItem>
              </SelectContent>
            </Select>

            <p className="text-xs text-slate-400 mt-2">
              Changes the font throughout the FinLend dashboard.
              PDF and loan agreement fonts remain unchanged.
            </p>

            <div
              className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-4"
              style={{
                fontFamily:
                  form.font_style === "outfit"
                    ? "'Outfit', sans-serif"
                    : form.font_style === "arial"
                    ? "Arial, sans-serif"
                    : form.font_style === "georgia"
                    ? "Georgia, serif"
                    : form.font_style === "times"
                    ? "'Times New Roman', Times, serif"
                    : "'Inter', sans-serif"
              }}
            >
              <div className="text-sm font-semibold text-navy">
                FinLend Font Preview
              </div>
              <div className="text-sm text-slate-600 mt-1">
                Customer Loan • EMI Collection • ₹10,000
              </div>
            </div>
          </div>

        </div>
      ))}

      {card("PDF / Print Settings", (
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
            <span className="text-sm font-medium text-navy">Show business logo on PDFs</span>
            <Switch checked={!!form.print_show_logo} onCheckedChange={(v) => set("print_show_logo", v)} data-testid="settings-print-logo" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
            <span className="text-sm font-medium text-navy">Show terms &amp; conditions on PDFs</span>
            <Switch checked={!!form.print_show_terms} onCheckedChange={(v) => set("print_show_terms", v)} data-testid="settings-print-terms" />
          </div>
        </div>
      ))}

      {card("Backup / Export Data", (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">Download all customers, loans, payments and settings as a JSON file.</p>
          <Button variant="outline" onClick={exportBackup} data-testid="export-backup-json-button">
            <Download className="h-4 w-4 mr-2" /> Export JSON
          </Button>
        </div>
      ))}
    </div>
  );
}
